import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';

import { DriveFolderRef } from '../model/driveFolders';
import { PlanLine } from '../model/driveSummary';
import { DriveCopyRequest, DrivePreviewRequest, DriveShareRequest } from '../model/types';
import { ArtworkDrive } from './ArtworkDrive';

const useArtworkDriveService = () => {
  const { keycloak } = useKeycloak();
  const token = keycloak.token;

  return useMemo(() => {
    const client = new ArtworkDrive({ headers: { Authorization: `Bearer ${token}` } });

    return {
      getOrderDriveSummary: async (orderId: string, signal?: AbortSignal) =>
        (await client.getOrderDriveSummary(orderId, { signal })).data,
      listFolderAccess: async (orderId: string, folder: DriveFolderRef, signal?: AbortSignal) =>
        (await client.listFolderAccess(orderId, folder, { signal })).data,
      shareFolder: async (orderId: string, folder: DriveFolderRef, request: DriveShareRequest) =>
        (await client.shareFolder(orderId, folder, request)).data,
      unshareFolder: async (orderId: string, folder: DriveFolderRef, permissionId: string) => {
        await client.unshareFolder(orderId, folder, permissionId);
      },
      uploadIntent: async (orderId: string, folder: DriveFolderRef) =>
        (await client.uploadIntent(orderId, folder)).data,
      refreshFolder: async (orderId: string, folder: DriveFolderRef) =>
        (await client.refreshFolder(orderId, folder)).data,
      copyToDecoration: async (orderId: string, request: DriveCopyRequest) =>
        (await client.copyToDecoration(orderId, request)).data,
      generatePreviews: async (
        orderId: string,
        operationId: string,
        request: DrivePreviewRequest,
      ) => (await client.generatePreviews(orderId, operationId, request)).data,
      importWeTransfer: async (orderId: string, folder: DriveFolderRef, link: string) => {
        await client.importWeTransfer(orderId, folder, link);
      },
      getPlanDriveSummary: async (planId: string, signal?: AbortSignal) =>
        (await client.getPlanDriveSummary(planId, { signal })).data,
      listPlanAccess: async (planId: string, signal?: AbortSignal) =>
        (await client.listPlanAccess(planId, { signal })).data,
      sharePlan: async (planId: string, request: DriveShareRequest) =>
        (await client.sharePlan(planId, request)).data,
      unsharePlan: async (planId: string, permissionId: string) => {
        await client.unsharePlan(planId, permissionId);
      },
      importPlanWeTransfer: async (planId: string, link: string, line?: PlanLine) => {
        await client.importPlanWeTransfer(planId, link, line);
      },
    };
  }, [token]);
};

export default useArtworkDriveService;
